import uuid from 'uuid/v4'
import {
  V3_API_TARGET,
  origin,
  API_ROOT as DEFAULT_APP_API_ROOT,
  OP_SECRET_KEY_AUTHORIZATION,
  NODE_ENV,
  OP_API_BASE_URL
} from 'config/server'
import _request from '@jarvis.oddle/core/tools/request'
import {
  arrayToJSON,
  toJSON,
  stripSecureFlag,
  getCookie
} from '@jarvis.oddle/core/tools/cookie'
import { extract } from '@jarvis.oddle/core/tools/object'
import { getIP } from '@jarvis.oddle/core/tools/ip'
import { isEmpty } from '@jarvis.oddle/core/tools/string'
import { makeQueryString } from '@jarvis.oddle/core/tools/routes'
import { logApiError } from '@jarvis.oddle/core/tools/error'
import logger from '@jarvis.oddle/core/tools/logger'
import {
  metaPath,
  userPath,
  menuPath,
  storeOrderPath,
  unavailableResourcePath,
  getAccessTokenPath,
  getOPAddressesPath
} from './common'
import { postLogApi } from '@jarvis.oddle/core/api/newrelic/client'
import { parseJwt } from 'tools/jwtParser'

const makeTargetUri = (name) => {
  return name ? V3_API_TARGET.replace(/api.*?\./, `${name}.`) : V3_API_TARGET
}

const makeHeaders = (req) => ({
  Cookie: req.get('Cookie'),
  'User-Agent': req.get('User-Agent'),
  Referer: req.get('Referer'),
  'Content-Type': 'application/json',
  Accept: 'application/json',
  'X-Forwarded-For': getIP(req),
  'Custom-Url': getCookie(req.get('Cookie') || '', 'Custom-Url'),
  // Custom header for OMS to identify the caller page URL
  // There is no standard header that fit our use case: https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers
  'Loaded-Url': req.get('Loaded-Url'),
  'X-Request-Id': req.headers['X-Request-Id'] || uuid(),
  'X-Oddle-Pass-Token': req.headers['x-oddle-pass-token'],
  'X-Oddle-Pass-Client-Id': req.headers['x-oddle-pass-client-id']
})

const request = async (req, res, options, logsRequest) => {
  const requestCookie = req.get('Cookie') || ''
  const storeOrderId = getCookie(requestCookie, 'storeOrderId') || ''

  const identifiedQs = {
    ...options.qs,
    _soid: !isEmpty(storeOrderId) ? storeOrderId : ''
  }

  const identifiedOptions = {
    ...options,
    qs: identifiedQs
  }

  logger.verbose(
    `[visitorid ${req.cookies && req.cookies.visitorid}] ${
      req.url
    } requested ${JSON.stringify(options, null, 2)}`
  )
  const response = await _request(identifiedOptions)
  const isHTML = response.body.indexOf('<!DOCTYPE html>') > -1
  logger.verbose(`[visitorid ${req.cookies && req.cookies.visitorid}] ${req.url}
    got response ${isHTML ? '<html>...</html>' : response.body} ${
    response.statusCode
  }
    ${isHTML ? `with cookie ${response.headers['set-cookie']}` : ''}
  `)
  // log all request for specific api
  if (logsRequest) {
    postLogApi({
      req,
      origin,
      apiRoot: DEFAULT_APP_API_ROOT,
      payload: {
        message: `fesc requested ${JSON.stringify({
          ...identifiedQs,
          uri: options.uri,
          body: options.body
        })} got response ${response.body} ${response.statusCode}`.substring(
          0,
          1000
        )
      }
    }).catch((_) => _)
  } else {
    await logApiError({
      req,
      response,
      origin,
      apiRoot: DEFAULT_APP_API_ROOT,
      extendLogs: `token ${req.headers['x-oddle-pass-token']} and got`
    })
  }

  const cookie = stripSecureFlag(response.headers['set-cookie'])
  if (cookie) res.setHeader('set-cookie', cookie)

  const cacheControl = response.headers['cache-control']
  if (cacheControl) res.setHeader('cache-control', cacheControl)

  const pragma = response.headers['pragma']
  if (pragma) res.setHeader('pragma', pragma)

  return response
}

// TODO: Reactor this to accept options object instead of multiple params
const call = async (
  req,
  res,
  uri,
  method = 'GET',
  body,
  next,
  logsRequest = false,
  useQuerystring = false
) => {
  try {
    const options = {
      uri,
      method,
      headers: makeHeaders(req),
      useQuerystring
    }
    if (body) options.body = JSON.stringify(body)
    const response = await request(req, res, options, logsRequest)

    res.setHeader('x-oddle-api', `${method} ${uri}`)
    return res.status(200).json(JSON.parse(response.body))
  } catch (error) {
    next(new Error(error))
  }
}

const getAvailablePromotionTarget =
  '/cart-api-v3/ajax-retrieve-available-promotions'
const getEligiblePromotionTarget =
  '/cart-api-v3/ajax-retrieve-eligible-promotions'
const getPromoCodeTarget =
  '/cart-api-v3/ajax-retrieve-promotion-from-promo-code'
const applyPromotionsTarget = '/cart-api-v3/ajax-apply-promotions'
const getStoresTarget = '/cart-api-v3/ajax-retrieve-stores'
const getTimeSlotsTarget = '/api/v4/cart/time-intervals'
const getStoreOrderTarget = '/cart-api/ajax-retrieve-storeOrder'
const updateCollectionTarget = '/cart-api-v3/ajax-update-collection-method'
const updatePickupLocationTarget = '/cart-api-v3/ajax-update-selected-store'
const updateDeliveryLocationTarget =
  '/cart-api-v3/ajax-update-customer-location'
const updateDeliveryAddressTwoTarget = '/cart-api-v3/storeOrder'
const updateCollectionTimeTarget =
  '/cart-api-v3/ajax-update-collection-date-time'
const getMenuTarget = '/cart-api-v3/ajax-retrieve-menu'
const addToCartTarget = '/cart-api-v3/ajax-add-to-cart'
const clearCartTarget = '/cart-api-v3/ajax-clear-cart'
const getAllStoresTarget = '/cart-api-v3/ajax-retrieve-all-stores'
export const updateOrderItemQuantityTarget =
  '/cart-api-v3/ajax-update-multiple-order-item-quantity'
const prepareCheckoutTarget = '/cart-api-v3/ajax-prepare-checkout'
const getCheckoutPaymentMethodsTarget =
  '/checkout/cart-v3/ajax-retrieve-payment-options'
const getCheckoutOnlinePaymentMethodsTarget =
  '/checkout/cart-v3/ajax-retrieve-online-payment-options'
export const loginTarget = '/customer-api-v3/ajax-login'
export const ocaLoginTarget = '/customer-api-v3/oddlepass:login'
export const logoutTarget = '/customer-api-v3/ajax-logout'
export const userFacebookInfoTarget = '/customer-api-v3/facebook-login-success'
export const createUserTarget = '/customer-api-v3/ajax-create-customer'
export const resetPasswordTarget =
  '/customer-api-v3/ajax-send-reset-password-instructions'
export const updateUserTarget = '/customer-api-v3/ajax-update-personal-settings'
export const updateUserPasswordTarget = '/customer-api-v3/ajax-update-password'
export const getUserOrderTarget =
  '/customer-api-v3/ajax-retrieve-customer-order'
export const createUserAddressTarget = '/customer-api-v3/ajax-create-address'
export const updateUserAddressTarget = '/customer-api-v3/ajax-update-address'
export const updateDefaultUserAddressTarget =
  '/customer-api-v3/ajax-set-default-billing-delivery-address'
export const removeUserAddressTarget = '/customer-api-v3/ajax-remove-address'
export const getCheckoutResultsTarget =
  '/retrieve-store-order-and-payment-status'
export const checkUserEmailTarget = '/customer-api-v3/users'
const getMetaTarget = '/api/v4/cart'
const checkoutTarget = '/api/v4/cart/checkout'
const getInvoiceTarget = '/api/v4/invoice'
const getTrackersTarget = '/cart-api-v3/menus/{menuId}/analytics-tracker'
const getScriptsTarget = '/cart-api-v3/ajax-get-shopping-cart-trackers'
const newsletterSignupTarget = '/api/v4/customers/newsletter/subscription'
export const verifyOTPTarget = '/api/loyalty/zap/otp/verify'
export const resendOTPTarget = '/api/loyalty/zap/otp/resend'
export const sendOTPTarget = '/api/loyalty/zap/otp/send'
export const createLoyaltyCustomerTarget = '/api/loyalty/zap/customer/register'
const getUnavailableResourceTarget = '/api/v4/unavailableResources/menus'
export const verifyAuthenticateTokenPath = (storeOrderId) =>
  `/api/v4/orders/${storeOrderId}/authenticate-token`
export const getDeliveryTrackingLinkPath = (storeOrderId) =>
  `/api/v4/orders/${storeOrderId}/track-delivery`
export const postStripeChargesPath = '/api/stripe/charges'
export const getTermsAndConditionPath = (menuId) =>
  `/api/v4/cart/${menuId}/terms-and-conditions`
export const getPrivacyPolicyPath = (menuId) =>
  `/api/v4/cart/${menuId}/privacy-policy`
export const userInfoTarget = '/customer-api-v3/ajax-retrieve-customer'
export const updateDeliveryDetailsPath = (storeOrderId) =>
  `/api/v4/cart/${storeOrderId}/delivery-details`
export const getAddressFormattingPath = (menuId) =>
  `/api/v4/cart/${menuId}/address-format`
export const getFeaturesOfMenuPath = (menuId) =>
  `/api/v4/menus/${menuId}/features`
export const getStoreOrderMetaDataPath = (storeOrderId) =>
  `/api/v4/orders/${storeOrderId}/metadata`
export const getMenuFeaturesPath = (menuId) =>
  `/api/v4/menus/${menuId}/features`
export const getStripeTokenPath = `/api/v4/payment-options/stripe-token`
export const updateCheckoutMetaPath = (storeOrderId) =>
  `/api/v4/cart/${storeOrderId}`
export const removeSelectedVoucherPath = (voucherId) =>
  `/api/v4/cart/vouchers/${voucherId}`

export function createApi(express, apiRoot) {
  const router = new express.Router()

  router.get(`${metaPath}/:subdomain`, async (req, res, next) => {
    try {
      const { storeId, referrer } = req.query
      const options = {
        uri: `${makeTargetUri(
          req.params.subdomain,
          req
        )}${getMetaTarget}${makeQueryString({
          'store-id': storeId,
          referrer
        })}`,
        method: 'GET',
        headers: makeHeaders(req)
      }

      const response = await request(req, res, options)
      const cookies = arrayToJSON(response.headers['set-cookie'] || [])
      const cookieFields = [
        'storeOrderId',
        'menuId',
        'hasPickup',
        'hasDelivery',
        'lang',
        'userInfo',
        '_o'
      ]
      const resData = extract(cookies, cookieFields)
      // Backend does not send storeOrderId back if there is storeOrderId in the request cookie
      const reqData = extract(toJSON(req.get('Cookie') || ''), cookieFields)
      const json = JSON.parse(response.body)
      json.data = { ...json.data, ...reqData, ...resData }
      return res.status(200).json(json)
    } catch (error) {
      next(new Error(error))
    }
  })

  router.post(userFacebookInfoTarget, (req, res, next) => {
    call(
      req,
      res,
      `${makeTargetUri(req.query.cartName, req)}${userFacebookInfoTarget}`,
      'POST',
      req.body,
      next
    )
  })

  // Proxy for checkUserExistenceApi
  // Can not use call() direct here because if we put email on URI, it will get encode.
  // Because backend not do the decoding as it should now. So we have to put it in query string.
  router.get(checkUserEmailTarget, async (req, res, next) => {
    try {
      const response = await request(req, res, {
        uri: `${makeTargetUri(undefined, req)}${checkUserEmailTarget}`,
        qs: { email: req.query.email, 'menu-id': req.query.menuId },
        method: 'GET'
      })
      const json = JSON.parse(response.body)
      return res.status(200).json(json.data)
    } catch (error) {
      next(new Error(error))
    }
  })

  // Proxy for getUserApi
  router.get(userPath, (req, res, next) =>
    call(
      req,
      res,
      `${makeTargetUri(req.query.subdomain, req)}${userInfoTarget}`,
      undefined,
      undefined,
      next
    )
  )

  // Proxy for createUserApi
  router.post(userPath, (req, res, next) =>
    call(
      req,
      res,
      `${makeTargetUri(
        req.query.subdomain,
        req
      )}${createUserTarget}?store-order-id=${req.body.storeOrderId}`,
      'POST',
      req.body,
      next
    )
  )

  router.post(getUserOrderTarget, (req, res, next) =>
    call(
      req,
      res,
      `${makeTargetUri(req.body.subdomain, req)}${getUserOrderTarget}`,
      'POST',
      {
        'customer-id': req.body.customerId,
        'menu-id': req.body.menuId,
        'store-order-id': req.body.orderId
      },
      next
    )
  )

  router.post(createUserAddressTarget, (req, res, next) =>
    call(
      req,
      res,
      `${makeTargetUri(req.body.subdomain, req)}${createUserAddressTarget}`,
      'POST',
      {
        customerId: req.body.customerId,
        autoCompleteAddress: req.body.address.autoCompleteAddress || '',
        address: req.body.address.address || '',
        addressTwo: req.body.address.addressTwo || '',
        city: req.body.address.city || '',
        state: req.body.address.state || '',
        country: req.body.address.country || '',
        postal: req.body.address.postal || '',
        latitude:
          req.body.address.latitude !== undefined
            ? req.body.address.latitude
            : null,
        longitude:
          req.body.address.longitude !== undefined
            ? req.body.address.longitude
            : null
      },
      next
    )
  )

  router.post(updateUserAddressTarget, (req, res, next) =>
    call(
      req,
      res,
      `${makeTargetUri(undefined, req)}${updateUserAddressTarget}`,
      'POST',
      {
        id: req.body.addressId,
        customerId: req.body.customerId,
        autoCompleteAddress: req.body.address.autoCompleteAddress || '',
        address: req.body.address.address || '',
        addressTwo: req.body.address.addressTwo || '',
        city: req.body.address.city || '',
        state: req.body.address.state || '',
        country: req.body.address.country || '',
        postal: req.body.address.postal || '',
        latitude:
          req.body.address.latitude !== undefined
            ? req.body.address.latitude
            : null,
        longitude:
          req.body.address.longitude !== undefined
            ? req.body.address.longitude
            : null
      },
      next
    )
  )

  router.post(removeUserAddressTarget, (req, res, next) =>
    call(
      req,
      res,
      `${makeTargetUri(undefined, req)}${removeUserAddressTarget}`,
      'POST',
      {
        'address-id': req.body.addressId,
        'customer-id': req.body.customerId
      },
      next
    )
  )

  router.post(updateDefaultUserAddressTarget, (req, res, next) =>
    call(
      req,
      res,
      `${makeTargetUri(undefined, req)}${updateDefaultUserAddressTarget}`,
      'POST',
      {
        'address-id': req.body.addressId,
        'customer-id': req.body.userId,
        'is-billing': false,
        'menu-id': req.body.menuId
      },
      next
    )
  )

  router.post(updateUserTarget, (req, res, next) =>
    call(
      req,
      res,
      `${makeTargetUri(req.query.subdomain, req)}${updateUserTarget}`,
      'POST',
      {
        'customer-id': req.body.customerId,
        'menu-id': req.body.menuId,
        email: req.body.email,
        'first-name': req.body.firstName,
        'last-name': req.body.lastName,
        'organisation-name': req.body.organisationName,
        phone: req.body.phone,
        receiveEmail: req.body.receiveEmail,
        'additional-phone': req.body.additionalPhone,
        salutation: req.body.salutation,
        source: req.body.source,
        gender: req.body.gender,
        dob: req.body.dob,
        acceptMarketing: req.body.acceptMarketing,
        acceptMarketingMenu: req.body.acceptMarketingMenu
      },
      next
    )
  )

  router.post(updateUserPasswordTarget, (req, res, next) =>
    call(
      req,
      res,
      `${makeTargetUri(req.query.subdomain, req)}${updateUserPasswordTarget}`,
      'POST',
      {
        'customer-id': req.body.customerId,
        'menu-id': req.body.menuId,
        'new-password': req.body.newPassword,
        'new-repassword': req.body.reNewPassword,
        'old-password': req.body.password
      },
      next
    )
  )

  // Proxy for userLoginApi
  router.post(loginTarget, (req, res, next) =>
    call(
      req,
      res,
      `${makeTargetUri(
        req.query.subdomain,
        req
      )}${loginTarget}?store-order-id=${req.body.storeOrderId}`,
      'POST',
      {
        email: req.body.email,
        password: req.body.password,
        'menu-id': req.body.menuId,
        source: req.body.source
      },
      next
    )
  )

  // Proxy for fetchOcaUserApi
  router.post(ocaLoginTarget, (req, res, next) =>
    call(
      req,
      res,
      `${makeTargetUri(req.query.subdomain, req)}${ocaLoginTarget}?menu-id=${
        req.body.menuId
      }`,
      'POST',
      {
        email: req.body.email,
        password: req.body.password
      },
      next
    )
  )

  router.post(logoutTarget, (req, res, next) =>
    call(
      req,
      res,
      `${makeTargetUri(
        req.query.subdomain,
        req
      )}${logoutTarget}?store-order-id=${req.body.storeOrderId}`,
      'POST',
      {
        'store-order-id': req.body.storeOrderId
      },
      next
    )
  )

  router.post(resetPasswordTarget, (req, res, next) =>
    call(
      req,
      res,
      `${makeTargetUri(req.query.subdomain, req)}${resetPasswordTarget}`,
      'POST',
      {
        email: req.body.email,
        'menu-id': req.body.menuId
      },
      next
    )
  )

  router.post(`${userPath}/verifyOTP`, (req, res, next) =>
    call(
      req,
      res,
      `${makeTargetUri(undefined, req)}${verifyOTPTarget}?menu-id=${
        req.query.menuId
      }`,
      'POST',
      {
        OTP: req.body.OTP,
        OTPSessionId: req.body.OTPSessionId,
        phone: req.body.phone
      },
      next
    )
  )

  router.post(resendOTPTarget, (req, res, next) =>
    call(
      req,
      res,
      `${makeTargetUri(undefined, req)}${resendOTPTarget}?menu-id=${
        req.query.menuId
      }`,
      'POST',
      {
        OTPSessionId: req.body.OTPSessionId
      },
      next
    )
  )

  router.post(sendOTPTarget, (req, res, next) =>
    call(
      req,
      res,
      `${makeTargetUri(undefined, req)}${sendOTPTarget}?menu-id=${
        req.query.menuId
      }`,
      'POST',
      {
        purpose: req.body.purpose,
        phone: req.body.phone
      },
      next
    )
  )

  router.post(createLoyaltyCustomerTarget, (req, res, next) =>
    call(
      req,
      res,
      `${makeTargetUri(undefined, req)}${createLoyaltyCustomerTarget}?menu-id=${
        req.query.menuId
      }`,
      'POST',
      {
        'customer-id': req.body.customerId
      },
      next
    )
  )

  router.get(`${menuPath}/:menuId/stores`, (req, res, next) =>
    call(
      req,
      res,
      `${makeTargetUri(undefined, req)}${getAllStoresTarget}?menu-id=${
        req.params.menuId
      }`,
      undefined,
      undefined,
      next
    )
  )

  router.get(getTermsAndConditionPath(':menuId'), (req, res, next) =>
    call(
      req,
      res,
      `${makeTargetUri(undefined, req)}${getTermsAndConditionPath(
        req.params.menuId
      )}`,
      undefined,
      undefined,
      next
    )
  )

  router.get(`${menuPath}/:menuId/trackers`, (req, res, next) =>
    call(
      req,
      res,
      `${makeTargetUri(undefined, req)}${getTrackersTarget.replace(
        /{menuId}/,
        req.params.menuId
      )}`,
      undefined,
      undefined,
      next
    )
  )

  router.get(`${menuPath}/:menuId/scripts`, (req, res, next) =>
    call(
      req,
      res,
      `${makeTargetUri(undefined, req)}${getScriptsTarget}?menu-id=${
        req.params.menuId
      }`,
      undefined,
      undefined,
      next
    )
  )

  router.get(`${menuPath}/:menuId`, (req, res, next) =>
    call(
      req,
      res,
      `${makeTargetUri(undefined, req)}${getMenuTarget}?menu-id=${
        req.params.menuId
      }&store-order-id=${req.query.storeOrderId}&mode=${req.query.mode}`,
      undefined,
      undefined,
      next
    )
  )

  router.get(`${storeOrderPath}/:storeOrderId/promotions`, (req, res, next) =>
    call(
      req,
      res,
      `${makeTargetUri(
        undefined,
        req
      )}${getEligiblePromotionTarget}?store-order-id=${
        req.params.storeOrderId
      }`,
      undefined,
      undefined,
      next
    )
  )

  router.post(`${storeOrderPath}/:storeOrderId/promotions`, (req, res, next) =>
    call(
      req,
      res,
      `${makeTargetUri(undefined, req)}${applyPromotionsTarget}`,
      'POST',
      {
        storeOrderId: req.params.storeOrderId,
        promotions: req.body.promotions
      },
      next,
      true
    )
  )

  router.get(
    `${storeOrderPath}/:storeOrderId/promotions/all`,
    (req, res, next) =>
      call(
        req,
        res,
        `${makeTargetUri(
          undefined,
          req
        )}${getAvailablePromotionTarget}?store-order-id=${
          req.params.storeOrderId
        }&mode=${req.params.mode}`,
        undefined,
        undefined,
        next
      )
  )

  router.get(
    `${storeOrderPath}/:storeOrderId/promotions/:code`,
    (req, res, next) => {
      return call(
        req,
        res,
        `${makeTargetUri(undefined, req)}${getPromoCodeTarget}?store-order-id=${
          req.params.storeOrderId
        }&promo-code=${encodeURIComponent(req.params.code)}`,
        undefined,
        undefined,
        next
      )
    }
  )

  router.get(`${storeOrderPath}/:storeOrderId/stores`, (req, res, next) =>
    call(
      req,
      res,
      `${makeTargetUri(undefined, req)}${getStoresTarget}${makeQueryString({
        'store-order-id': req.params.storeOrderId,
        'store-id': req.query.storeId
      })}`,
      undefined,
      undefined,
      next
    )
  )

  router.put(updateOrderItemQuantityTarget, (req, res, next) =>
    call(
      req,
      res,
      `${makeTargetUri(undefined, req)}${updateOrderItemQuantityTarget}`,
      'PUT',
      {
        'store-order-id': req.body.storeOrderId,
        items: req.body.items
      },
      next
    )
  )

  router.get(`${storeOrderPath}/:storeOrderId/slots/:date`, (req, res, next) =>
    call(
      req,
      res,
      `${makeTargetUri(undefined, req)}${getTimeSlotsTarget}?store-order-id=${
        req.params.storeOrderId
      }&selected-date=${req.params.date}`,
      undefined,
      undefined,
      next
    )
  )

  router.put(`${storeOrderPath}/:storeOrderId/slots`, (req, res, next) =>
    call(
      req,
      res,
      `${makeTargetUri(undefined, req)}${updateCollectionTimeTarget}`,
      'POST',
      {
        'store-order-id': req.params.storeOrderId,
        'collection-date': req.body.collectionDate,
        'collection-time-interval': req.body.collectionTimeSlot,
        'selected-time-option-id': req.body.collectionTimeSlotId
      },
      next
    )
  )

  router.put(`${storeOrderPath}/:storeOrderId/collection`, (req, res, next) =>
    call(
      req,
      res,
      `${makeTargetUri(undefined, req)}${updateCollectionTarget}`,
      'POST',
      {
        'store-order-id': req.params.storeOrderId,
        'is-pickup': req.body.isPickup,
        'store-id': req.query.storeId
      },
      next
    )
  )

  router.put(`${storeOrderPath}/:storeOrderId/store`, (req, res, next) =>
    call(
      req,
      res,
      `${makeTargetUri(undefined, req)}${updatePickupLocationTarget}`,
      'POST',
      {
        'store-order-id': req.params.storeOrderId,
        'store-id': req.body.storeId,
        lat: req.body.lat,
        long: req.body.long,
        'selected-date': req.body.selectedDate
      },
      next
    )
  )

  router.put(`${storeOrderPath}/:storeOrderId/address`, (req, res, next) => {
    const address = req.body.address || {}
    return call(
      req,
      res,
      `${makeTargetUri(undefined, req)}${updateDeliveryLocationTarget}`,
      'POST',
      {
        'address-id': address.selectedAddressId,
        'address-auto-complete': address.deliveryAddress || '',
        'address-one': address.deliveryAddressOne || '',
        'address-two': address.deliveryAddressTwo || '',
        'address-city': address.deliveryAddressCity || '',
        'address-state': address.deliveryAddressState || '',
        'address-country': address.deliveryAddressCountry || '',
        'address-postal': address.deliveryAddressPostal || '',
        latitude: `${address.latitude}`,
        longitude: `${address.longitude}`,
        'address-two-details': address.addressTwoDetails || null,
        'store-order-id': req.body.storeOrderId,
        'store-id': req.query.storeId
      },
      next
    )
  })

  router.put(`${storeOrderPath}/:storeOrderId/addressTwo`, (req, res, next) => {
    return call(
      req,
      res,
      `${makeTargetUri(undefined, req)}${updateDeliveryAddressTwoTarget}/${
        req.body.id
      }/addressTwo`,
      'PUT',
      req.body,
      next
    )
  })

  router.post(`${storeOrderPath}/:storeOrderId/items`, (req, res, next) =>
    call(
      req,
      res,
      `${makeTargetUri(undefined, req)}${addToCartTarget}`,
      'POST',
      {
        storeOrderId: req.params.storeOrderId,
        orderItem: req.body
      },
      next
    )
  )

  router.delete(`${storeOrderPath}/:storeOrderId/items`, (req, res, next) =>
    call(
      req,
      res,
      `${makeTargetUri(undefined, req)}${clearCartTarget}`,
      'POST',
      {
        'store-order-id': req.params.storeOrderId
      },
      next
    )
  )
  router.get(
    `${storeOrderPath}/:storeOrderId/checkout/prepare`,
    (req, res, next) =>
      call(
        req,
        res,
        `${makeTargetUri(req.query.subdomain, req)}${prepareCheckoutTarget}`,
        'POST',
        {
          'store-order-id': req.params.storeOrderId
        },
        next
      )
  )

  router.get(getCheckoutResultsTarget, (req, res, next) =>
    call(
      req,
      res,
      `${makeTargetUri(
        undefined,
        req
      )}${getCheckoutResultsTarget}?storeOrderId=${req.query.storeOrderId}`,
      undefined,
      undefined,
      next
    )
  )

  router.get(
    `${storeOrderPath}/:storeOrderId/checkout/results`,
    (req, res, next) =>
      call(
        req,
        res,
        `${makeTargetUri(
          undefined,
          req
        )}${getCheckoutResultsTarget}?storeOrderId=${req.params.storeOrderId}`,
        undefined,
        undefined,
        next
      )
  )

  router.get(`${storeOrderPath}/:storeOrderId/checkout`, (req, res, next) =>
    call(
      req,
      res,
      `${makeTargetUri(undefined, req)}${getMetaTarget}/${
        req.params.storeOrderId
      }`,
      undefined,
      undefined,
      next
    )
  )

  router.get(
    `${storeOrderPath}/:storeOrderId/invoice/:hashCode`,
    (req, res, next) =>
      call(
        req,
        res,
        `${makeTargetUri(undefined, req)}${getInvoiceTarget}/${
          req.params.storeOrderId
        }/${req.params.hashCode}`,
        undefined,
        undefined,
        next
      )
  )

  router.post(`${storeOrderPath}/:storeOrderId/checkout`, (req, res, next) =>
    call(
      req,
      res,
      `${makeTargetUri(undefined, req)}${checkoutTarget}`,
      'POST',
      req.body,
      next
    )
  )

  router.get(`${storeOrderPath}/:storeOrderId/payments/all`, (req, res, next) =>
    call(
      req,
      res,
      `${makeTargetUri(
        undefined,
        req
      )}${getCheckoutPaymentMethodsTarget}?store-order-id=${
        req.params.storeOrderId
      }`,
      undefined,
      undefined,
      next
    )
  )

  router.get(
    `${storeOrderPath}/:storeOrderId/payments/online`,
    (req, res, next) =>
      call(
        req,
        res,
        `${makeTargetUri(
          undefined,
          req
        )}${getCheckoutOnlinePaymentMethodsTarget}?store-order-id=${
          req.params.storeOrderId
        }`,
        undefined,
        undefined,
        next
      )
  )

  router.get(`${storeOrderPath}/:storeOrderId`, (req, res, next) =>
    call(
      req,
      res,
      `${makeTargetUri(undefined, req)}${getStoreOrderTarget}`,
      'POST',
      {
        'store-order-id': req.params.storeOrderId,
        'store-id': req.query.storeId
      },
      next
    )
  )

  router.post(`${userPath}/newsletter/subscription`, (req, res, next) =>
    call(
      req,
      res,
      `${makeTargetUri(undefined, req)}${newsletterSignupTarget}`,
      'POST',
      req.body,
      next
    )
  )

  router.get(`${unavailableResourcePath}/:menuId`, (req, res, next) =>
    call(
      req,
      res,
      `${makeTargetUri(undefined, req)}${getUnavailableResourceTarget}/${
        req.params.menuId
      }`,
      undefined,
      undefined,
      next
    )
  )

  router.post(postStripeChargesPath, (req, res, next) =>
    call(
      req,
      res,
      `${makeTargetUri(undefined, req)}${postStripeChargesPath}`,
      'POST',
      req.body,
      next
    )
  )

  router.post(verifyAuthenticateTokenPath(':storeOrderId'), (req, res, next) =>
    call(
      req,
      res,
      `${makeTargetUri(undefined, req)}${verifyAuthenticateTokenPath(
        req.params.storeOrderId
      )}`,
      'POST',
      req.body,
      next
    )
  )

  router.get(getDeliveryTrackingLinkPath(':storeOrderId'), (req, res, next) =>
    call(
      req,
      res,
      `${makeTargetUri(undefined, req)}${getDeliveryTrackingLinkPath(
        req.params.storeOrderId
      )}`,
      undefined,
      undefined,
      next
    )
  )

  router.get(getPrivacyPolicyPath(':menuId'), (req, res, next) =>
    call(
      req,
      res,
      `${makeTargetUri(undefined, req)}${getPrivacyPolicyPath(
        req.params.menuId
      )}`,
      undefined,
      undefined,
      next
    )
  )

  // Proxy for fetchUserViaOddlePassApi
  router.get(userInfoTarget, async (req, res, next) => {
    try {
      const options = {
        uri: `${makeTargetUri(
          req.query.subdomain,
          req
        )}${userInfoTarget}${makeQueryString({
          loggedOut: req.query.loggedOut
        })}`,
        method: 'GET',
        qs: { token: req.headers['x-oddle-pass-token'] },
        headers: makeHeaders(req)
      }

      const response = await request(req, res, options, true)
      postLogApi({
        req,
        origin,
        apiRoot: DEFAULT_APP_API_ROOT,
        payload: {
          message:
            `fesc requested ${options.uri} from ${req.headers['user-agent']} with ${req.headers['x-oddle-pass-token']}`.substring(
              0,
              1000
            )
        }
      }).catch((_) => _)

      res.setHeader(
        'x-oddle-api',
        `GET ${makeTargetUri(req.query.subdomain, req)}${userInfoTarget}`
      )
      return res.status(200).json(JSON.parse(response.body))
    } catch (error) {
      next(new Error(error))
    }
  })

  // Proxy for fetchMetaAddressFormatApi
  router.get(getAddressFormattingPath(':menuId'), async (req, res, next) =>
    call(
      req,
      res,
      `${makeTargetUri(undefined, req)}${getAddressFormattingPath(
        req.params.menuId
      )}`,
      undefined,
      undefined,
      next
    )
  )

  router.put(updateDeliveryDetailsPath(':storeOrderId'), (req, res, next) =>
    call(
      req,
      res,
      `${makeTargetUri(undefined, req)}${updateDeliveryDetailsPath(
        req.params.storeOrderId
      )}`,
      'PUT',
      req.body,
      next
    )
  )

  router.get(getMenuFeaturesPath(':menuId'), async (req, res, next) => {
    const queryString = req.query.features.reduce(
      (string, feature) => `${string}&features%5B%5D=${feature}`,
      ''
    )
    return call(
      req,
      res,
      `${makeTargetUri(undefined, req)}${getMenuFeaturesPath(
        req.params.menuId
      )}?${queryString}`,
      undefined,
      undefined,
      next,
      false,
      true
    )
  })

  router.get(getStoreOrderMetaDataPath(':storeOrderId'), (req, res, next) =>
    call(
      req,
      res,
      `${makeTargetUri(undefined, req)}${getStoreOrderMetaDataPath(
        req.params.storeOrderId
      )}`,
      undefined,
      undefined,
      next
    )
  )

  router.get('/payment-options/stripe-token/:menuId', (req, res, next) =>
    call(
      req,
      res,
      `${makeTargetUri(
        undefined,
        req
      )}/api/v4/payment-options/stripe-token?menuId=${req.params.menuId}`,
      undefined,
      undefined,
      next
    )
  )

  router.get(getStripeTokenPath, (req, res, next) =>
    call(
      req,
      res,
      `${makeTargetUri(undefined, req)}${getStripeTokenPath}?menuId=${
        req.query.menuId
      }${req.query.testAccount ? `&testAccount=${req.query.testAccount}` : ''}`,
      undefined,
      undefined,
      next
    )
  )

  router.post(getAccessTokenPath, async (req, res, next) => {
    try {
      const options = {
        uri: `${OP_API_BASE_URL}${getAccessTokenPath}`,
        method: 'POST',
        headers: {
          ...makeHeaders(req),
          ...(NODE_ENV === 'development'
            ? {
                Authorization: `Bearer ${OP_SECRET_KEY_AUTHORIZATION}`
              }
            : {})
        },
        body: JSON.stringify({ ...req.body, responseType: 'token' })
      }
      const response = await request(req, res, options)
      let cookie = response.headers['set-cookie']
      if (NODE_ENV === 'development') {
        cookie = (response.headers['set-cookie'] || []).map((c) =>
          c
            .replace(/ secure;?/, '')
            .replace(/domain=(.*?);/, 'domain=localhost;')
        )
      }
      if (cookie) res.setHeader('set-cookie', cookie)
      const json = JSON.parse(response.body)
      return res.status(200).json(json)
    } catch (error) {
      next(new Error(error))
    }
  })

  router.get(getOPAddressesPath, async (req, res, next) => {
    try {
      const accessToken = req.headers['x-oddle-pass-token']
      let customerId
      if (accessToken) {
        ;({ customerId } = parseJwt(accessToken, true))
      }
      const options = {
        uri: `${OP_API_BASE_URL}${getOPAddressesPath}?customer-id=${customerId}`,
        method: 'GET',
        headers: {
          ...makeHeaders(req),
          Authorization: `Bearer ${OP_SECRET_KEY_AUTHORIZATION}`
        },
        body: JSON.stringify({ ...req.body, responseType: 'token' })
      }
      const response = await request(req, res, options)
      const json = JSON.parse(response.body)
      return res.status(200).json(json)
    } catch (error) {
      next(new Error(error))
    }
  })

  router.patch(`${storeOrderPath}/:storeOrderId`, async (req, res, next) => {
    try {
      const options = {
        uri: `${makeTargetUri(undefined, req)}${getMetaTarget}/${
          req.params.storeOrderId
        }`,
        method: 'PATCH',
        headers: {
          ...makeHeaders(req)
        },
        body: JSON.stringify({ ...req.body })
      }
      const response = await request(req, res, options)
      const json = JSON.parse(response.body)
      return res.status(200).json(json)
    } catch (error) {
      next(new Error(error))
    }
  })

  router.delete(removeSelectedVoucherPath(':voucherId'), (req, res, next) => {
    return call(
      req,
      res,
      `${makeTargetUri(undefined, req)}${removeSelectedVoucherPath(
        req.params.voucherId
      )}?store-order-id=${req.query.storeOrderId}`,
      'DELETE',
      {},
      next
    )
  })

  return [apiRoot, router]
}
